import {useEffect, useRef} from "react";
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext";
import {$generateHtmlFromNodes, $generateNodesFromDOM} from "@lexical/html";
import {$createParagraphNode, $getRoot, $insertNodes} from "lexical";

const parser = new DOMParser();

function hydrateFromHtml(editor, html)
{
    editor.update(() =>
    {
        const root = $getRoot();
        root.clear();

        const dom = parser.parseFromString(html || '', 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);

        $getRoot().select();
        $insertNodes(nodes);

        if ($getRoot().getChildrenSize() === 0)
        {
            $getRoot().append($createParagraphNode());
        }
    }, {tag: 'html-sync'});
}

// Bridges Lexical's internal EditorState with a plain HTML string, so callers
// (react-hook-form Controller, or a plain useState) never need to know Lexical exists.
// A ref tracks the last HTML this component itself produced, so external value changes
// (e.g. form reset) can be told apart from the component's own edits bouncing back in.
const HtmlSyncPlugin = ({value, onChange}) =>
{
    const [editor] = useLexicalComposerContext();
    const lastRef = useRef(undefined);

    useEffect(() =>
    {
        if (value === lastRef.current)
        {
            return;
        }

        hydrateFromHtml(editor, value);
        lastRef.current = value;
    }, [value, editor]);

    useEffect(() => editor.registerUpdateListener(({editorState, dirtyElements, dirtyLeaves, tags}) =>
    {
        if (tags.has('html-sync'))
        {
            return;
        }

        if (dirtyElements.size === 0 && dirtyLeaves.size === 0)
        {
            return;
        }

        editorState.read(() =>
        {
            const html = $generateHtmlFromNodes(editor, null);
            lastRef.current = html;
            onChange(html);
        });
    }), [editor, onChange]);

    return null;
}

export {HtmlSyncPlugin};
